import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Ponderacion } from "@/lib/api";
import {
  aPayloadPestana,
  calcularPestana,
  conAlturas,
  conMultiplicadores,
  conPrecio,
  formPestana,
  hayCambiosPaso1,
  hayCambiosPaso2,
  hayCambiosPrecio,
  hayErroresPaso1,
  hayErroresPaso2,
  validarPestana,
  type CampoDepto,
  type ErroresPestana,
  type FormPestana,
} from "@/lib/ponderacion";

const SIN_ERRORES: ErroresPestana = {};

type Cuerpo = ReturnType<typeof aPayloadPestana> & { ponderados_guardados?: true; homologados_guardados?: true };
type Combinar = (base: FormPestana, de: FormPestana) => FormPestana;

/**
 * Lógica de la pestaña Ponderación (sin JSX): paso 1, multiplicadores por depto → m² ponderados; paso 2, coef. altura
 * de cada depto (arranca en el estándar de su piso) → m² homologados y % del fideicomiso; abajo, siempre a la vista,
 * el resumen con el precio base USD por m² homologado → valor de cada unidad y recaudación total. Todo se recalcula en
 * vivo. Cada parte se guarda con su botón (los pasos 1 y 2, una vez guardados, se muestran plegados; Mati, 2026-10-10)
 * y lo que está sin guardar de las otras sobrevive.
 */
export function usePonderacionTab() {
  const { fid } = useContextoApp();
  const ruta = `/api/fideicomisos/${fid}/unidades/ponderacion`;
  const recurso = useApiResource<Ponderacion>(ruta);
  const [original, setOriginal] = useState<FormPestana | null>(null);
  const [form, setForm] = useState<FormPestana | null>(null);
  const [marcas, setMarcas] = useState({ ponderados: false, homologados: false });
  const enviar = (cuerpo: Cuerpo) => api.put<Ponderacion>(ruta, cuerpo);

  useEffect(() => {
    if (!recurso.data) return;
    const f = formPestana(recurso.data);
    setOriginal(f);
    setForm(f);
    setMarcas({ ponderados: recurso.data.ponderados_guardados, homologados: recurso.data.homologados_guardados });
  }, [recurso.data]);

  const calculo = useMemo(() => (form ? calcularPestana(form) : null), [form]);
  const errores = form ? validarPestana(form) : SIN_ERRORES;

  /** Una parte de la pestaña con su Guardar y su Descartar: `con` toma esa parte de un form y la pone sobre otro. */
  function useParte(con: Combinar, hayCambiosEn: (f: FormPestana, o: FormPestana) => boolean, conErrores: boolean, extra: Partial<Cuerpo> = {}) {
    const accion = useAsyncAction(enviar);
    const [vuelta, setVuelta] = useState(0); // cambia en cada guardado: vuelve a montar el desplegable, que queda plegado
    const [guardado, setGuardado] = useState(false);
    const cambios = form !== null && original !== null && hayCambiosEn(form, original);
    useEffect(() => {
      if (cambios) setGuardado(false);
    }, [cambios]);
    const guardar = async (e: FormEvent) => {
      e.preventDefault();
      if (!form || !original || conErrores) return;
      const actualizada = await accion.run({ ...aPayloadPestana(con(original, form)), ...extra });
      if (!actualizada) return;
      const servidor = formPestana(actualizada);
      setOriginal(servidor);
      // Lo de esta parte queda como lo devolvió el servidor; lo pendiente de las otras, como estaba.
      setForm((f) => (f ? con(f, servidor) : servidor));
      setMarcas({ ponderados: actualizada.ponderados_guardados, homologados: actualizada.homologados_guardados });
      setVuelta((n) => n + 1);
      setGuardado(true);
    };
    const descartar = () => {
      if (original) setForm((f) => (f ? con(f, original) : f));
      accion.limpiarError();
    };
    return { cambios, guardar, descartar, guardando: accion.loading, error: accion.error, guardado, vuelta };
  }

  const paso1 = useParte(conMultiplicadores, hayCambiosPaso1, hayErroresPaso1(errores), { ponderados_guardados: true });
  const paso2 = useParte(conAlturas, hayCambiosPaso2, hayErroresPaso2(errores), { homologados_guardados: true });
  const precio = useParte(conPrecio, hayCambiosPrecio, errores.precio_base !== undefined);

  const cambiarDepto = (id: number, campo: CampoDepto, valor: string) =>
    setForm((f) => (f ? { ...f, departamentos: f.departamentos.map((d) => (d.id === id ? { ...d, [campo]: valor } : d)) } : f));
  const cambiarOtra = (id: number, valor: string) =>
    setForm((f) => (f ? { ...f, otras: f.otras.map((o) => (o.id === id ? { ...o, multiplicador: valor } : o)) } : f));
  const cambiarPrecioBase = (valor: string) => setForm((f) => (f ? { ...f, precio_base: valor } : f));

  return {
    cargando: form === null && recurso.loading,
    errorCarga: form === null ? (recurso.error?.message ?? null) : null,
    reintentar: recurso.recargar,
    form,
    calculo,
    porDefecto: recurso.data ?? null,
    hayOtras: (form?.otras.length ?? 0) > 0,
    cambiarDepto,
    cambiarOtra,
    cambiarPrecioBase,
    // En vivo (sin esperar a Guardar): si no, el cálculo quedaría en "—" sin explicación.
    errores,
    paso1: { ...paso1, plegado: marcas.ponderados },
    paso2: { ...paso2, plegado: marcas.homologados },
    precio,
  };
}
